import { SetStateAction } from 'react';

import { GridPaginationModel } from '@mui/x-data-grid';
import { useSearchParams } from 'react-router-dom';

import { queryParameters } from '~constants';

/**
 * A hook to use pagination that keeps its state within the url
 * Notice that the first page for the grid = 0, but the first page
 * for the url = 1
 * @param defaultPageSize
 * @param disablePageUrlSync A boolean to turn off synchronization with the url
 * @returns
 */
const useSearchParamsPagination = (
	defaultPageSize = 10
): [GridPaginationModel, (model: SetStateAction<GridPaginationModel>) => void] => {
	const [searchParams, setSearchParams] = useSearchParams();

	const pageFromUrl = Number(searchParams.get(queryParameters.Page) ?? '0');
	const pageSizeFromUrl = Number(searchParams.get(queryParameters.PageSize));

	/**
	 * The pagination model that is synced with the url
	 */
	const paginationModel = {
		page: pageFromUrl > 1 ? pageFromUrl - 1 : 0,
		pageSize: pageSizeFromUrl > 0 ? pageSizeFromUrl : defaultPageSize,
	};

	const setPaginationModel = (modelNew: SetStateAction<GridPaginationModel>) => {
		const model =
			typeof modelNew === 'function' ?
				modelNew({ page: paginationModel.page, pageSize: paginationModel.pageSize })
			:	modelNew;

		setSearchParams((prev) => {
			if (model.page > 0) {
				prev.set(queryParameters.Page, (model.page + 1).toString());
			} else {
				prev.delete(queryParameters.Page);
			}

			if (model.pageSize > 0) {
				prev.set(queryParameters.PageSize, model.pageSize.toString());
			}

			return prev;
		});
	};

	return [paginationModel, setPaginationModel];
};

export default useSearchParamsPagination;
